import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Constants } from '@praktio/common';

import { RootState } from '../../../ducks/configureStore';
import { Correctness, Instructions, Tooltips } from '../../../utils/constants';
import TryAgainPill from './TryAgainPill';
import FeedbackBadge from './FeedbackBadge';
import FeedbackText from './FeedbackText';
import InfoPill from '../InfoPill/InfoPill';

import './FeedbackBar.scss';
import { Toast } from 'react-bootstrap';

const { CataTypes, TemplateTypes } = Constants;
const CATAEditTypes = [CataTypes.EditHorizontal, CataTypes.EditVertical];

interface IProps {
  feedbackPanelWidth?: number;
  windowSize: string;
}

const FeedbackBar = ({ feedbackPanelWidth, windowSize }: IProps) => {
  // REDUX STATE
  const {
    activityComplete,
    correct,
    numOfAttempts,
    slideType,
    slideTypeIsSingleSelect,
    triesAllowed,
    slideSubType,
    paragraphSelection,
    layoutIsHorizontal,
  } = useSelector((state: RootState) => {
    const { slidesLitmos, quizLitmos } = state;
    const { currentSlide } = slidesLitmos;
    const {
      learnerData,
      isSingle: slideTypeIsSingleSelect,
      tries: triesAllowed,
      type: slideType,
      subType: slideSubType,
      paragraphSelection,
    } = currentSlide || {};
    const { tries: numOfAttempts } = quizLitmos;
    const { completed: activityComplete, correct } = learnerData || {};
    const layoutIsHorizontal = slideSubType === CataTypes.EditHorizontal;
    return {
      activityComplete,
      correct,
      numOfAttempts,
      slideType,
      slideTypeIsSingleSelect,
      triesAllowed,
      slideSubType,
      paragraphSelection,
      layoutIsHorizontal,
    };
  });

  const triesRemaining =
    typeof triesAllowed === 'number' && typeof numOfAttempts === 'number'
      ? triesAllowed - numOfAttempts
      : undefined;

  // If exercise is completed, show an icon that reflects correct/incorrectness
  // If exercise is incomplete, show number of tries remaining
  const badgeContent = activityComplete
    ? correct
      ? Correctness.Correct
      : Correctness.Incorrect
    : triesRemaining
    ? triesRemaining.toString()
    : '';

  // If exercise is completed, show text that reflects correct/incorrectness
  // If exercise is incomplete, show "Try/Tries remaining"
  const mainText = activityComplete
    ? correct
      ? 'Correct'
      : 'Incorrect'
    : triesRemaining
    ? triesRemaining > 1
      ? 'Tries left'
      : 'Try left'
    : '';

  // Show "Try Again" if: not out of tries & not completed.
  const [showTryAgain, setShowTryAgain] = useState(
    !!(!activityComplete && numOfAttempts && numOfAttempts > 0),
  );
  useEffect(() => {
    if (!activityComplete && numOfAttempts && numOfAttempts > 0) {
      setShowTryAgain(true);
    }
    if (activityComplete) {
      setShowTryAgain(false);
    }
  }, [activityComplete, numOfAttempts]);

  // Genereate Tooltip and Instruction text
  const tooltipType = activityComplete ? Tooltips.Feedback : Tooltips.Quiz;
  const instructionsType = activityComplete ? Instructions.Feedback : Instructions.Quiz;
  let tooltipText = '';
  let instructionsText = '';
  // CATA
  if (slideType === TemplateTypes.CheckAllApply) {
    // Edits
    if (CATAEditTypes.includes(slideSubType || '')) {
      if (slideTypeIsSingleSelect) {
        if (layoutIsHorizontal) {
          tooltipText = tooltipType.CATASingleSelectEdits_Horizontal;
          instructionsText = instructionsType.CATASingleSelectEdits_Horizontal;
        } else {
          tooltipText = tooltipType.CATASingleSelectEdits_Vertical;
          instructionsText = instructionsType.CATASingleSelectEdits_Vertical;
        }
      } else {
        if (layoutIsHorizontal) {
          tooltipText = tooltipType.CATAMultiSelectEdits_Horizontal;
          instructionsText = instructionsType.CATAMultiSelectEdits_Horizontal;
        } else {
          tooltipText = tooltipType.CATAMultiSelectEdits_Vertical;
          instructionsText = instructionsType.CATAMultiSelectEdits_Vertical;
        }
      }
      // Standard
    } else {
      if (slideTypeIsSingleSelect) {
        tooltipText = tooltipType.CATASingleSelect;
        instructionsText = instructionsType.CATASingleSelect;
      } else {
        tooltipText = tooltipType.CATAMultiSelect;
        instructionsText = instructionsType.CATAMultiSelect;
      }
    }
    // CBS
  } else if (slideType === TemplateTypes.ClickBestSpot) {
    // Paragraph
    // TODO - Fully implement paragraph/multi-select when ready.
    if (!!paragraphSelection) {
      tooltipText = tooltipType.CBSParagraphSingle;
      instructionsText = instructionsType.CBSParagraphSingle;
      // Standard
    } else {
      tooltipText = tooltipType.CBS;
      instructionsText = instructionsType.CBS;
    }
  }

  return (
    <div className={classNames('FeedbackBar', windowSize, { static: true })}>
      <div className={classNames('FeedbackBar-left-content')}>
        <FeedbackBadge content={badgeContent} />
        <FeedbackText content={mainText} />
        <Toast
          onClose={() => setShowTryAgain(false)}
          show={showTryAgain}
          className={classNames('TryAgainPill-wrapper', windowSize)}
          delay={3000}
          autohide
        >
          <TryAgainPill />
        </Toast>
      </div>
      <div className={classNames('FeedbackBar-right-content')}>
        <InfoPill
          instructions={instructionsText}
          tooltipText={tooltipText}
          activityComplete={!!activityComplete}
          layoutIsHorizontal={layoutIsHorizontal}
          feedbackPanelWidth={feedbackPanelWidth}
          windowSize={windowSize}
        />
      </div>
    </div>
  );
};

export default FeedbackBar;
